import { useRef, useState } from 'react';
import { Trash2 } from 'lucide-react';
import { useCompany } from '@/company/context';
import { useSessionBoundFetch } from '@/lib/use-session-guard';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { OverflowMenu } from '@/components/ui/menu';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import deletionCopy from '@/i18n/ns/library-deletion';
import { overlaysCopy } from '@/i18n/ns/overlays';
import { useWorkspace } from '../context';
import { notifyLibraryChanged } from '../artifacts/library-changed';
import { libraryUploads } from '../library-feed/upload-queue';
import { itemArchiveApi, type ArchiveKind } from './api';
import { archiveError } from './errors';

type DeleteItemProps = {
  kind: ArchiveKind;
  id: string;
  title: string;
  canDelete?: boolean;
};

export function DeleteLibraryItem(props: DeleteItemProps) {
  const company = useCompany();
  const ws = useWorkspace();
  return (
    <ScopedDeleteItem key={`${company.data?.company?.id}:${ws.user?.id}:${props.id}`} {...props} />
  );
}

function ScopedDeleteItem({ kind, id, title, canDelete }: DeleteItemProps) {
  const ws = useWorkspace();
  const session = useRef(useSessionBoundFetch()).current;
  const trigger = useRef<HTMLSpanElement>(null);
  const copy = useNamespaceCopy(deletionCopy);
  const overlays = useNamespaceCopy(overlaysCopy);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  if (!canDelete) return null;

  async function remove() {
    const current = ws.beginNavigation();
    setBusy(true);
    setError('');
    try {
      await itemArchiveApi.delete(kind, id, session);
      if (!current()) return;
      if (kind === 'file') {
        libraryUploads.forgetFile(id);
      }
      ws.setTurnContext({
        ...ws.turnContext,
        fileIds:
          kind === 'file'
            ? ws.turnContext.fileIds.filter((fileId) => fileId !== id)
            : ws.turnContext.fileIds,
        resultIds:
          kind === 'result'
            ? ws.turnContext.resultIds.filter((resultId) => resultId !== id)
            : ws.turnContext.resultIds,
        selection:
          ws.turnContext.selection?.artifactId === id ? undefined : ws.turnContext.selection,
      });
      if (ws.canvas?.id === id) ws.openCanvas(null);
      setConfirming(false);
      notifyLibraryChanged();
      ws.notify(copy.deleted);
      await ws.refresh();
    } catch (failure) {
      if (current()) setError(archiveError(failure, copy));
    } finally {
      // Local cleanup belongs to this keyed component, not the navigation
      // intent: Back must not strand a mounted dialog in its pending state.
      setBusy(false);
      if (!current()) setConfirming(false);
    }
  }

  return (
    <>
      <span ref={trigger}>
        <OverflowMenu
          label={formatCopy(overlays.moreActionsFor, { name: title })}
          items={[
            {
              key: 'delete',
              label: copy.delete,
              icon: <Trash2 size={15} />,
              tone: 'danger',
              onSelect: () =>
                ws.confirmLeave(() => {
                  setError('');
                  setConfirming(true);
                }),
            },
          ]}
        />
      </span>
      {confirming && (
        <ConfirmDialog
          className="ws-library-delete-dialog"
          title={formatCopy(copy.title, { name: title })}
          description={copy.description}
          confirmLabel={busy ? copy.deleting : copy.delete}
          busy={busy}
          error={error}
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            trigger.current?.querySelector('button')?.focus();
          }}
          onCancel={() => setConfirming(false)}
          onConfirm={() => void remove()}
        />
      )}
    </>
  );
}
